<script setup lang="ts">
import { computed, onMounted, PropType, Ref, ref, watch } from "vue";
import { DirectInputAPI, LanguageManagementAPI, ProfileAdministrationAPI } from "../../api";
import {
    AssociatedDirectInput,
    Department,
    DirectLink,
    Participant,
    ProductLanguages,
    Project,
    ProjectRole,
    Trainer,
    TranslatedProduct,
} from "../../types/PersologOnlineTypes.ts";
import {
    buildDirectLinkMap,
    formatDateBound,
    getTranslatedProductName as translateProductName,
    hasParticipant,
    ProjectListItem,
    surfaceToListItem,
} from "../../utils/ProjectOverviewUtils.ts";
import { i18n } from "../../main.ts";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import DataViewSkeleton from "../common/DataViewSkeleton.vue";
import DeletionConfirmation from "../common/DeletionConfirmation.vue";
import ProjectDetailsView from "./ProjectDetailsView.vue";
import ProjectInformation from "./ProjectInformation.vue";
import { ensureBenchmarkReportFavorited, extractLanguages, updateProducts } from "../../utils/ProductViewUtils.ts";
import ConfigureProject from "./configure-tabs/ConfigureProject.vue";
import { useWindowSize } from "../../composables/useWindowSize";
import { isMobileView, setQueryParameter } from "../../utils/GeneralUtils.ts";
import { useRoute, useRouter } from "vue-router";
import { getProductNames } from "../../utils/ProjectUtils.ts";
import { ProjectSortMode, useProjectsStore } from "../../stores/projects.ts";
import { useProductsWithRolesStore } from "../../stores/productsWithRoles";

type LayoutType = 'list' | 'grid';

const emit = defineEmits(["layout"]);

const props = defineProps({
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    layout: {
        type: String as PropType<LayoutType>,
        default: "list",
    },
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const projectsStore = useProjectsStore();

const gridOptions = ref(["list", "grid"]);

const products = ref<TranslatedProduct[]>([]);
const participantsMap = ref<Record<number, Participant[]>>({});
const directInputMap = ref<Record<number, AssociatedDirectInput>>({});
const departmentsMap = ref<Record<number, Department[]>>({});
const rolesMap = ref<Record<number, ProjectRole[]>>({});

// Load layout preference from localStorage or default to props value
const savedLayout = localStorage.getItem('projectView.layout') as LayoutType;
const layout = ref(savedLayout || props.layout);
const languages: Ref<ProductLanguages | null> = ref(null);
const productTranslations: Ref<Record<string, string>> = ref({});

// Filter / sort state bound to the toolbar; changes are pushed to the store which reloads page 0.
const search = ref<string>("");
const dates = ref<[Date, Date] | null>(null);
const archive = ref<boolean>(route.query.archived === "true");
const sortMode = ref<string>("lastUpdated");
const sortOrder = ref<"asc" | "desc">("desc");
const productFilter = ref<string[]>([]);
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Variables to store the id of a project that should be imported.
 * importProjectID is the id of the project that should be imported.
 */
const importProjectID: Ref<number | null> = ref(null);
const importGroupChartID: Ref<number | null> = ref(null);
const importProject = computed(() =>
    importProjectID.value == null ? undefined : projectsStore.detailCache[importProjectID.value],
);

/**
 * The id of a project that should be shown in the view (e.g. after creation or via a deep link).
 */
const shownProjectId = ref<string | undefined>(route.query.projectId?.valueOf() as string | undefined);
/**
 * The id of a participant to link directly to that participant's profile.
 */
const participantId = ref<string | undefined>(route.query.participantId?.valueOf() as string | undefined);

const loading = computed(() => projectsStore.loading);
const totalRecords = computed(() => projectsStore.totalRecords);
const hasArchivedProjects = computed(() => projectsStore.hasArchivedProjects);

const projects = computed<ProjectListItem[]>(() => projectsStore.items.map(surfaceToListItem));

/**
 * Direct links for the current page, derived from each project's embedded surface summary — no
 * separate fetch. Reactive to the page, so created/edited/deleted links reflect on the next reload.
 */
const directLinkMap = computed<Record<number, DirectLink>>(() => buildDirectLinkMap(projects.value));

/**
 * Returns the full (core) project of a visible project, or undefined while it is not loaded yet.
 * The details view must not open on the surface card data: it lacks `trainer`, so a profile
 * download from there would request `/profile/undefined/...` and be rejected (RDT-496).
 */
function shownDetail(id: number): Project | undefined {
    return isVisible(id) ? projectsStore.detailCache[id] : undefined;
}

const sortOptions = computed(() => [
    { label: t("project-overview.sort.options.lastUpdated"), value: "lastUpdated" },
    { label: t("project-overview.sort.options.creationDate"), value: "creationDate" },
]);

const productFilterOptions = computed(() =>
    products.value.map((product) => ({
        label: getTranslatedProductName(product.productNo),
        value: product.productNo.toLowerCase(),
    })),
);

function getTranslatedProductName(product: string): string {
    return translateProductName(productTranslations.value, product);
}

/**
 * Loads the auxiliary (core-ish) data needed by the cards for the current page only, in the
 * background. Because the overview now holds a single page, this no longer scales with the total
 * number of projects.
 */
async function enrichCurrentPage() {
    const pageProjects = projectsStore.items;
    if (pageProjects.length === 0) {
        directInputMap.value = {};
        return;
    }
    // Direct links are not fetched here: they ride along on the project surface (directLinkMap is
    // derived from it), so the card and details view have them as soon as the page loads.
    await Promise.all([fetchParticipants(), fetchDirectInputs(), fetchRoles()]);

    // When deep-linking to a participant, load the full data of the project that contains them so
    // the auto-opened detail view has its core data.
    if (participantId.value) {
        const match = pageProjects.find((project) =>
            hasParticipant(participantsMap.value[project.id], participantId.value),
        );
        if (match) {
            await projectsStore.getDetail(match.id);
        }
    }
}

async function fetchParticipants() {
    await Promise.all(
        projectsStore.items.map(async (project) => {
            participantsMap.value[project.id] = await ProfileAdministrationAPI.getParticipants(project.id);
        }),
    );
}

/**
 * Update the participants for a specific project.
 */
async function updateParticipants(participants: Participant[], projectId: number) {
    participantsMap.value[projectId] = participants;
    products.value = await updateProducts();
}

async function fetchDirectInputs() {
    try {
        const ids = projectsStore.items.map((project) => project.id);
        if (ids.length === 0) {
            directInputMap.value = {};
            return;
        }
        const directInputs = await DirectInputAPI.getDirectInputsGroup(props.trainer?.id, ids);
        directInputMap.value = Object.fromEntries(directInputs.map((directInput) => [directInput.id, directInput]));
    } catch (error) {
        console.error("Failed to fetch direct inputs:", error);
        directInputMap.value = {};
    }
}

async function fetchRoles() {
    const store = useProductsWithRolesStore();
    await store.ensureLoaded();
    await Promise.all(
        projectsStore.items.map(async (project) => {
            if (store.productHasRoles(project.product)) {
                rolesMap.value[project.id] = await ProfileAdministrationAPI.getRolesForProject(project.id);
            }
        }),
    );
}

/**
 * Shows the project with the given id, loading its full (core) data first.
 */
async function showProject(projectId: number) {
    await projectsStore.getDetail(projectId);
    setQueryParameter(route, router, "projectId", projectId);
    shownProjectId.value = projectId.toString();
}

/**
 * Updates the view after a project has been deleted.
 */
async function handleProjectDelete(id: number) {
    projectsStore.invalidateDetail(id);
    shownProjectId.value = undefined;
    await projectsStore.reload();
    await enrichCurrentPage();
    products.value = await updateProducts();
}

async function handleProjectUpdate(updatedProject: Project) {
    projectsStore.cacheDetail(updatedProject);
    await projectsStore.reload();
}

/**
 * Handles the creation of a new project: caches its full data, reloads the first page (the new
 * project sorts first by last-updated) and opens it immediately. A created direct-link project's
 * link rides along on the reloaded surface (directLinkMap is derived from it), so the details view
 * shows the link right away without a separate fetch or any loading state.
 */
async function handleProjectCreation(createdProject: Project) {
    projectsStore.cacheDetail(createdProject);
    await projectsStore.loadPage(0);
    if (createdProject.id !== undefined) {
        shownProjectId.value = createdProject.id.toString();
    }
    products.value = await updateProducts();
}

function handleDepartmentsUpdate(projectID: number, departmentsList: Department[]) {
    departmentsMap.value[projectID] = departmentsList;
}

function onPage(event: { page: number; rows: number }) {
    projectsStore.pageSize = event.rows;
    projectsStore.loadPage(event.page);
}

// --- Push toolbar state into the store (each change reloads page 0) ---

watch(search, () => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => projectsStore.setSearch(search.value), 300);
});

watch(productFilter, (value) => {
    projectsStore.setProducts(value ?? []);
});

watch(sortMode, (value) => {
    const mode: ProjectSortMode = value === "creationDate" ? "creation" : "edited";
    projectsStore.setSort(mode);
});

watch(sortOrder, (value) => {
    projectsStore.setDirection(value);
});

watch(archive, (value) => {
    projectsStore.setArchived(value);
});

watch(dates, (value) => {
    if (!value || !value[0]) {
        projectsStore.setDateRange(undefined, undefined);
        return;
    }
    const [start, end] = value;
    projectsStore.setDateRange(formatDateBound(start, false), formatDateBound(end ?? start, true));
});

// Reload the page's auxiliary data whenever the page content changes.
watch(() => projectsStore.items, () => {
    enrichCurrentPage();
});

watch(
    () => props.layout,
    (newLayout: LayoutType) => {
        layout.value = newLayout;
    },
);

watch(
    () => layout.value,
    () => {
        emit("layout", layout.value);
    },
);

watch(
    () => i18n.global.locale.value,
    async () => {
        productTranslations.value = await getProductNames();
        await projectsStore.reload();
    },
);

// always switch to grid on mobile
const windowSize = useWindowSize();
const isDesktop = computed(() => windowSize.width >= 640);
watch(isDesktop, (newIsDesktop) => {
    if (newIsDesktop) {
        const savedLayout = localStorage.getItem('projectView.layout') as LayoutType;
        layout.value = savedLayout || props.layout;
    } else {
        layout.value = "grid";
    }
});

// Save layout preference to localStorage whenever it changes
watch(layout, (newLayout) => {
    if (newLayout) {
        localStorage.setItem('projectView.layout', newLayout);
    }
}, { immediate: false });

onMounted(async () => {
    await projectsStore.initialize(props.trainer.id, archive.value);
    // Load a deep-linked project first: its details view only opens once the full project is there.
    if (shownProjectId.value) {
        try {
            await projectsStore.getDetail(Number(shownProjectId.value));
        } catch (error) {
            console.error("Failed to load the deep-linked project:", error);
        }
    }
    products.value = await updateProducts();
    if (await ensureBenchmarkReportFavorited(products.value)) {
        // in122 was just favorited — refetch so it shows in the stock list and the import option
        products.value = await updateProducts();
    }
    productTranslations.value = await getProductNames();
    const productLanguages = await LanguageManagementAPI.fetchLanguages();
    languages.value = extractLanguages(productLanguages);

    // Use saved preference or fallback to default behavior
    const savedLayout = localStorage.getItem('projectView.layout') as LayoutType;
    layout.value = isDesktop.value ? (savedLayout || "list") : "grid";
});

async function openBenchmark(projectNo: number, groupChartId?: number) {
    await projectsStore.getDetail(projectNo);
    importProjectID.value = projectNo;
    importGroupChartID.value = groupChartId ?? projectNo;
}

/**
 * Checks if a project is visible.
 * A project is visible if it is the shown project or if the participantId is part of the project.
 */
function isVisible(id: number) {
    return id === Number(shownProjectId.value) || hasParticipant(participantsMap.value[id], participantId.value);
}

const openConsultationLink = () => {
    globalThis.open(t('accessibility.consultation.url'), '_blank', 'noopener,noreferrer');
};

/**
 * True when the trainer has no projects at all (as opposed to no results for the active filters).
 * Must also account for archived projects: a trainer whose only project is archived still "has"
 * a project — showing the first-project empty state here would hide the archive toggle and strand
 * them with no way back to the archived view.
 */
const noProjectsAtAll = computed(
    () =>
        !loading.value &&
        totalRecords.value === 0 &&
        !projectsStore.hasActiveFilters &&
        !archive.value &&
        !projectsStore.hasArchivedProjects,
);
</script>

<template>
    <div id="main-content" tabindex="-1" class="header-section mt-8">
        <!-- Consultation button - shown above title on mobile -->
        <div class="consultation-button-mobile md:hidden mt-2">
            <Button
                class="consultation-btn p-4 font-bold"
                icon="pi pi-external-link"
                icon-pos="right"
                :label="t('accessibility.consultation.arrangeConsultation')"
                :aria-label="t('accessibility.consultation.arrangeConsultation') + ' ' + t('accessibility.links.externalLink')"
                @click="openConsultationLink"
            />
        </div>
        <!-- Mobile layout: Title only (button shown above) -->
        <div class="md:hidden">
            <TitleHeadline
                :title="archive ? t('project-overview.headingArchived') : t('project-overview.heading')"
                :subtitle="t('project-overview.subHeading')"
            />
        </div>

        <!-- Desktop layout: Title and button side by side -->
        <div class="hidden md:flex md:items-start md:justify-between md:gap-12">
            <div class="title-container">
                <TitleHeadline
                    :title="archive ? t('project-overview.headingArchived') : t('project-overview.heading')"
                    :subtitle="t('project-overview.subHeading')"
                    :top-margin="0"
                />
            </div>
            <Button
                class="mt-auto mb-auto ml-auto p-4 font-bold"
                icon="pi pi-external-link"
                icon-pos="right"
                :label="t('accessibility.consultation.arrangeConsultation')"
                :aria-label="t('accessibility.consultation.arrangeConsultation') + ' ' + t('accessibility.links.externalLink')"
                @click="openConsultationLink"
            />
        </div>
    </div>
    <!-- Header skeleton on first load -->
    <Skeleton v-if="loading && projects.length === 0" height="3rem"></Skeleton>
    <!-- Header no projects -->
    <Toolbar class="mb-4" v-else-if="noProjectsAtAll">
        <template #start>
            <ConfigureProject
                :trainer="trainer"
                :products="products"
                :no-label="true"
                :import-project="importProject"
                :import-group-chart-id="importGroupChartID"
                :in-project-overview="true"
                @update:project="handleProjectCreation"
                @update:import-project="importProjectID = null; importGroupChartID = null"
                @end:import-project="
                    shownProjectId = importProjectID?.toString();
                    importProjectID = null;
                    importGroupChartID = null;
                "
            />
        </template>
        <template #end>
            <h3 class="mr-6 my-2">{{ t("project-overview.productsEmpty") }}</h3>
        </template>
    </Toolbar>

    <!-- Header projects fetched -->
    <Panel v-else class="mb-4 panel-header-projects" :collapsed="false">
        <template #header>
            <div class="flex flex-wrap gap-4 w-full">
                <div class="basis-full md:basis-auto md:flex-none mt-2 md:mr-2">
                    <ConfigureProject
                        :trainer="trainer"
                        :products="products"
                        :import-project="importProject"
                        :import-group-chart-id="importGroupChartID"
                        :in-project-overview="true"
                        @update:project="handleProjectCreation"
                        @update:import-project="importProjectID = null; importGroupChartID = null"
                        @end:import-project="
                            shownProjectId = importProjectID?.toString();
                            importProjectID = null;
                            importGroupChartID = null;
                        "
                    />
                </div>
                <div class="basis-full md:flex-1 mt-2 flex justify-end pr-0">
                    <InputGroup class="p-input-group">
                        <Button id="project-search-btn" icon="pi pi-search" class="bg-persolog-blue" :aria-label="t('accessibility.buttons.search')" />
                        <InputText id="project-search-input" v-model="search" :placeholder="t('fields.searchPlaceholder')" :aria-label="t('accessibility.inputs.search')" />
                        <Button id="project-search-clear-btn" class="bg-persolog-blue" icon="pi pi-times" @click="search = ''" :aria-label="t('accessibility.buttons.clearSearch')" />
                    </InputGroup>
                </div>
            </div>
        </template>

        <div>
            <div class="flex flex-wrap gap-4 pt-4">
                <Divider class="m-0" align="left">
                    <b>{{ t("project-overview.filters") }}</b>
                </Divider>

                <!-- Sort Selection -->
                <div class="flex-1 flex flex-col justify-end">
                    <small>{{ t("project-overview.sort.label") }}:</small>
                    <InputGroup>
                        <Select
                            v-model="sortMode"
                            :options="sortOptions"
                            option-label="label"
                            option-value="value"
                            class="w-full"
                        />
                        <Button
                            :icon="sortOrder === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up-alt'"
                            class="bg-persolog-blue"
                            @click="sortOrder = sortOrder === 'desc' ? 'asc' : 'desc'"
                            :aria-label="t('project-overview.sort.order.' + sortOrder)"
                        />
                    </InputGroup>
                </div>

                <!-- Product Filter -->
                <div class="basis-full md:flex-1 flex flex-col justify-end">
                    <small>{{ t("project-overview.filterProducts.label") }}:</small>
                    <MultiSelect
                        v-model="productFilter"
                        display="chip"
                        filter
                        :options="productFilterOptions"
                        option-label="label"
                        option-value="value"
                        :placeholder="t('project-overview.productFilter')"
                        :max-selected-labels="3"
                        class="w-full"
                    />
                </div>

                <!-- Date Range Selection -->
                <div class="basis-full md:flex-1 flex flex-col justify-end">
                    <InputGroup>
                        <DatePicker
                            v-model="dates"
                            class="w-full"
                            show-icon
                            selection-mode="range"
                            icon-display="input"
                            :manual-input="false"
                            :touch-u-i="isMobileView()"
                            show-button-bar
                            :number-of-months="isMobileView() ? 1 : 2"
                            :date-format="t('formats.datePicker')"
                            :placeholder="t('project-overview.dateFilter')"
                        />
                        <Button class="bg-persolog-blue" icon="pi pi-times" @click="dates = null" :aria-label="t('accessibility.buttons.clearDate')" />
                    </InputGroup>
                </div>
                <!-- Keep the toggle visible in the archived view so the user can always switch back. -->
                <div v-if="archive || hasArchivedProjects" class="flex-1 flex flex-col justify-end">
                    <ToggleButton
                        id="project-archive-toggle"
                        v-model="archive"
                        :on-label="t('project-overview.archiveFilter.active')"
                        :off-label="t('project-overview.archiveFilter.archived')"
                        :aria-label="t('accessibility.toggles.archiveView')"
                        class="px-2"
                        :pt="{
                            label: {
                                style: { fontSize: '0.9em' },
                            },
                        }"
                    />
                </div>
                <!-- Layout Selection -->
                <div class="flex-1 flex flex-col justify-end">
                    <SelectButton
                        v-if="isDesktop"
                        v-model="layout"
                        :options="gridOptions"
                        :allow-empty="false"
                        :aria-label="t('accessibility.selectors.viewMode')"
                        class="flex-item ml-auto"
                    >
                        <template #option="{ option }">
                            <i :class="[option === 'list' ? 'pi pi-bars' : 'pi pi-th-large']" />
                        </template>
                    </SelectButton>
                </div>
            </div>
        </div>
    </Panel>

    <!-- Projects -->
    <div v-if="loading && projects.length === 0" class="mb-8">
        <DataViewSkeleton :layout="layout" />
    </div>
    <div v-else-if="!noProjectsAtAll" class="mb-8">
        <DataView
            :value="projects"
            :layout="layout"
            data-key="id"
            id="projects-view_data-view_all"
            lazy
            :paginator="totalRecords > 0"
            :rows="projectsStore.pageSize"
            :first="projectsStore.page * projectsStore.pageSize"
            :total-records="totalRecords"
            :rows-per-page-options="[20, 40, 60]"
            :loading="loading"
            @page="onPage"
        >
            <template #empty>
                <div class="p-6 text-center">{{ t("project-overview.noResults") }}</div>
            </template>

            <template #list="slotProps">
                <div
                    v-for="(item, index) in slotProps.items"
                    :id="'project-view_data-view_' + item.product + '-' + item.id"
                    :key="index"
                    class="flex product-card justify-between p-6 mt-1"
                >
                    <div class="flex flex-col flex-1">
                        <div class="flex items-center gap-2 mb-2">
                            <h3 class="mt-0 mb-0 text-2xl font-bold text-content">{{ item.name }}</h3>
                            <Tag
                                :data-cy="'participant-count-' + item.id"
                                severity="info"
                                icon="pi pi-users"
                                class="whitespace-nowrap participant-count-tag">{{ t('project-overview.participantCount', { count: item.participantCount }, item.participantCount) }}
                            </Tag>
                        </div>
                        <Tag severity="secondary" class="mb-2 w-fit">{{ getTranslatedProductName(item.product) }}</Tag>
                        <ProjectInformation
                            :project="item"
                            :direct-link="directLinkMap[item.id]"
                            :trainer="trainer"
                            :participants="participantsMap[item.id]"
                            :departments="departmentsMap[item.id]"
                            :roles="rolesMap[item.id]"
                        />
                    </div>
                    <div class="flex gap-2 flex-col items-end">
                        <DeletionConfirmation
                            :id="item.id"
                            :direct-link="directLinkMap[item.id] !== undefined"
                            :direct-input="directInputMap[item.id] !== undefined"
                            :name="'overview-' + item.id"
                            :message="t('project-details.deletion.message')"
                            :header="t('project-details.deletion.header')"
                            :type="'project-overview'"
                            @update:delete-project="handleProjectDelete"
                        />
                        <Button
                            class="width-200"
                            :id="`project-details-view_btn_open-${item.id}`"
                            icon="pi pi-eye"
                            @click="showProject(item.id)"
                            size="small"
                            :label="t('project-configuration.editButton')"
                        />
                        <ProjectDetailsView
                            v-if="shownDetail(item.id)"
                            :product="getTranslatedProductName(item.product)"
                            :project="shownDetail(item.id)!"
                            :participants="participantsMap[item.id]"
                            :direct-link="directLinkMap[item.id]"
                            :direct-input="directInputMap[item.id]"
                            :trainer="trainer"
                            :languages="languages"
                            :visible="isVisible(item.id)"
                            @update:delete="handleProjectDelete"
                            @update:participants="updateParticipants"
                            @update:project="handleProjectUpdate"
                            @update:departments="handleDepartmentsUpdate"
                            @reload:participants="updateParticipants"
                            @open:benchmark="openBenchmark"
                            @close="shownProjectId = undefined"
                        />
                    </div>
                </div>
            </template>

            <template #grid="slotProps">
                <div
                    v-for="(item, index) in slotProps.items"
                    :key="index"
                    class="product-card flex flex-col justify-between p-6 border-border-ui bg-surface"
                >
                    <h3 class="mt-0 mb-2 text-center text-2xl font-bold text-content">
                        {{ item.name }}
                    </h3>
                    <Tag severity="info" icon="pi pi-users" class="mb-2 self-center participant-count-tag">{{ t('project-overview.participantCount', { count: item.participantCount }, item.participantCount) }}</Tag>
                    <Tag severity="secondary" class="mb-2 self-center">{{ getTranslatedProductName(item.product) }}</Tag>
                    <ProjectInformation
                        :project="item"
                        :direct-link="directLinkMap[item.id]"
                        :trainer="trainer"
                        :participants="participantsMap[item.id]"
                    />
                    <ProjectDetailsView
                        v-if="shownDetail(item.id)"
                        :product="getTranslatedProductName(item.product)"
                        :project="shownDetail(item.id)!"
                        :participants="participantsMap[item.id]"
                        :direct-link="directLinkMap[item.id]"
                        :direct-input="directInputMap[item.id]"
                        :trainer="trainer"
                        :languages="languages"
                        :visible="isVisible(item.id)"
                        @update:delete="handleProjectDelete"
                        @update:participants="updateParticipants"
                        @update:project="handleProjectUpdate"
                        @update:departments="handleDepartmentsUpdate"
                        @reload:participants="updateParticipants"
                        @open:benchmark="openBenchmark"
                        @close="shownProjectId = undefined"
                    />

                    <Button
                        class="mt-2 mb-2"
                        :id="`project-details-view_btn_open-${item.id}`"
                        icon="pi pi-eye"
                        @click="showProject(item.id)"
                        size="small"
                        :label="t('project-configuration.editButton')"
                    />
                    <DeletionConfirmation
                        :id="item.id"
                        :direct-link="directLinkMap[item.id] !== undefined"
                        :direct-input="directInputMap[item.id] !== undefined"
                        :name="'overview-' + item.id"
                        :message="t('project-details.deletion.message')"
                        :header="t('project-details.deletion.header')"
                        :type="'project-overview'"
                        @update:delete-project="handleProjectDelete"
                    />
                </div>
            </template>
        </DataView>
    </div>
</template>

<style scoped>
.consultation-button-mobile {
    text-align: center;
}

.p-panel-header {
    padding-right: 0.6rem !important;
}

.panel-header-projects .p-panel-header {
    cursor: auto !important;
}

.project-panel {
    border: none !important;
}

.project-panel .p-panel-toggler {
    cursor: pointer !important;
    background-color: var(--p-surface-200);
}

.simple-label {
    font-size: 10px;
    margin-bottom: 5px;
    color: #7e868d;
}

.quantity-chip {
    border-radius: 6px;
    border: 1px solid var(--p-secondary-500);
    padding: 0 12px;
    text-align: center;
    background: var(--p-secondary-500);
    font-size: var(--p-button-sm-font-size);
    font-weight: bold;
    color: white;
}

.width-200 {
    width: 200px;
}
</style>
